import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import TextField from '@mui/material/TextField';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import { Button } from '@/ui';
import { TabPane } from '../TabPane';
import { useAffiliates, useDomains, useUserAttributes } from '../../../views/OfferBlocks/queries';
import { ColCondition, Condition, MultiselectField, NumberField, SelectRow, SelectField } from '../../FinalForm';
import { UserAttribute } from '../UserAttribute';
import { AffiliateComboBox } from '../components/AffiliateComboBox';
import { DomainAffiliate } from './DomainAffiliate';
import { useAffiliateTags } from '../../../views/Affiliates/queries';

const AttributePane = ({ collapse, push, source }) => {
  const generateName = (field) => (source ? `${source}.${field}` : field);
  const { data: userAttributes } = useUserAttributes(collapse, (data) => _.orderBy(data, ['name'], ['asc']));

  const { data: affiliates, isLoading: affiliatesLoading } = useAffiliates(true, (data) =>
    _.orderBy(data, ['name'], ['asc']).map(({ name, networkAffiliateId }) => ({
      id: networkAffiliateId,
      name,
    }))
  );

  const { data: affiliateTags, isLoading: affiliateTagsLoading } = useAffiliateTags('affiliate');

  const affiliateNames = useMemo(() => {
    if (affiliatesLoading || affiliateTagsLoading) return [];

    const tags =
      affiliateTags?.map((tag) => ({
        id: `#${tag.name}`,
        name: `#${tag.name}`,
      })) || [];

    return [...tags, ...(affiliates || [])];
  }, [affiliates, affiliateTags, affiliatesLoading, affiliateTagsLoading]);
  const { data: domains, isLoading: domainsLoading } = useDomains(collapse, (data) =>
    data.map((domain) => ({ id: domain.id, name: domain.name })).sort((a, b) => a.id - b.id)
  );

  const { data: domainTags, isLoading: domainTagsLoading } = useAffiliateTags('domain');

  const [domainSortDirection, setDomainSortDirection] = useState('asc');

  const domainsWithTags = useMemo(() => {
    if (domainsLoading || domainTagsLoading) return [];

    const orderedDomains = domainSortDirection === 'desc' ? [...(domains || [])].reverse() : domains || [];

    return [...(domainTags?.map((tag) => ({ id: `#${tag.name}`, name: `#${tag.name}` })) || []), ...orderedDomains];
  }, [domains, domainTags, domainsLoading, domainTagsLoading, domainSortDirection]);

  return (
    <TabPane tabId='Attribute'>
      <SelectRow name={generateName('criteriaIncludeOrExcludeUserAttributes')} label='User Attributes'></SelectRow>
      <Condition when={generateName('criteriaIncludeOrExcludeUserAttributes')} is={['Include', 'Exclude']}>
        <Grid size='grow'>
          <Field name={generateName('userAttributeRules')} component={UserAttribute} userAttributes={userAttributes} />
        </Grid>
      </Condition>
      {source !== 'publisherPrePingCriteria' && (
        <>
          <SelectRow name={generateName('criteriaIncludeOrExcludeAffNames')} label='Affiliate Names'>
            <ColCondition when={generateName('criteriaIncludeOrExcludeAffNames')}>
              <MultiselectField
                name={generateName('criteriaAffNames')}
                data={affiliateNames}
                textField={(item) =>
                  item.name && item.name.startsWith('#') ? `${item.id}` : `${item.id}: ${item.name}`
                }
                dataKey='name'
                busy={affiliatesLoading || affiliateTagsLoading}
                groupBy={(item) => (item.name.startsWith('#') ? 0 : 1)}
                renderListGroup={({ group }) => (group === 0 ? <span>Tags</span> : <span>Affiliates</span>)}
              />
            </ColCondition>
          </SelectRow>
          <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
            <Grid size={{ xs: 12, lg: 2 }}>
              <Typography variant='h5'>Aff SubID Configuration</Typography>
            </Grid>
            <Grid size={{ xs: 12, lg: 4 }}>
              <SelectField
                name={generateName('affSubIDConfiguration.andOrLogic')}
                options={[
                  {
                    value: '',
                    option: 'Select Logic',
                  },
                  {
                    value: 'AND',
                    option: 'All entries must pass',
                  },
                  {
                    value: 'OR',
                    option: 'Any of these entries can pass',
                  },
                  {
                    value: 'ANY',
                    option: 'Each entry evaluated independently',
                  },
                ]}
                parse={(value) => value || ''}
                format={(value) => value || ''}
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 10 }}>
              <FieldArray name={generateName('affSubIDConfiguration.subIdList')}>
                {({ fields }) => {
                  return (
                    <>
                      {fields.length === 0 ? (
                        <Condition when={generateName('affSubIDConfiguration.andOrLogic')} is={['AND', 'OR', 'ANY']}>
                          <Button
                            color='success'
                            startIcon={<Add />}
                            sx={{ mt: 2 }}
                            onClick={() =>
                              fields.push({
                                includeExclude: null,
                                affiliate: null,
                                subIds: '',
                              })
                            }
                          >
                            Add Entry
                          </Button>
                        </Condition>
                      ) : (
                        fields.map((fieldName, index) => {
                          return (
                            <Grid container spacing={2} key={fieldName} sx={{ mb: 2, alignItems: 'start' }}>
                              <Condition
                                when={generateName('affSubIDConfiguration.andOrLogic')}
                                is={['AND', 'OR', 'ANY']}
                              >
                                {fields.length > 1 && (
                                  <Grid size={{ xs: 12, lg: 1 }}>
                                    <Tooltip title='Remove Entry'>
                                      <IconButton
                                        color='error'
                                        type='button'
                                        aria-label='Remove Entry'
                                        onClick={() => fields.remove(index)}
                                      >
                                        <Close fontSize='small' />
                                      </IconButton>
                                    </Tooltip>
                                  </Grid>
                                )}
                                <Grid size={{ xs: 12, lg: 2 }}>
                                  <SelectField
                                    name={`${fieldName}.includeExclude`}
                                    options={[
                                      {
                                        value: null,
                                        option: 'None',
                                      },
                                      {
                                        value: 'Include',
                                        option: 'Include',
                                      },
                                      {
                                        value: 'Exclude',
                                        option: 'Exclude',
                                      },
                                    ]}
                                  />
                                </Grid>
                                <Grid size={{ xs: 12, lg: 4 }}>
                                  <Field
                                    name={`${fieldName}.affiliate`}
                                    component={AffiliateComboBox}
                                    affiliates={affiliateNames}
                                    busy={affiliatesLoading || affiliateTagsLoading}
                                  />
                                </Grid>
                                <Grid size={{ xs: 12, lg: 4 }}>
                                  <Field
                                    name={`${fieldName}.subIds`}
                                    render={({ input, meta }) => (
                                      <TextField
                                        {...input}
                                        placeholder='Enter Sub IDs (one per line)'
                                        multiline
                                        minRows={3}
                                        error={Boolean(meta.touched && meta.error)}
                                      />
                                    )}
                                  />
                                </Grid>
                                {index === fields.length - 1 && (
                                  <Grid size={{ xs: 12, lg: 1 }} offset={{ lg: 11 }}>
                                    <Tooltip title='Add Entry'>
                                      <IconButton
                                        color='success'
                                        aria-label='Add Entry'
                                        onClick={() =>
                                          fields.push({
                                            includeExclude: null,
                                            affiliate: null,
                                            subIds: '',
                                          })
                                        }
                                      >
                                        <Add />
                                      </IconButton>
                                    </Tooltip>
                                  </Grid>
                                )}
                              </Condition>
                            </Grid>
                          );
                        })
                      )}
                    </>
                  );
                }}
              </FieldArray>
            </Grid>
          </Grid>
          <DomainAffiliate
            domains={domainsWithTags}
            affiliateNames={affiliateNames}
            isLoading={domainsLoading || domainTagsLoading}
            domainSortDirection={domainSortDirection}
            onToggleDomainSortDirection={() => setDomainSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
          />
          <br />
        </>
      )}
      <SelectRow name={generateName('criteriaIncludeOrExcludeEmailDomains')} label='Email Domains'>
        <ColCondition when={generateName('criteriaIncludeOrExcludeEmailDomains')}>
          <MultiselectField
            name={generateName('criteriaEmailDomains')}
            data={[
              'aim.com',
              'aol.com',
              'gmail.com',
              'google.com',
              'hotmail.com',
              'live.com',
              'outlook.com',
              'sbcglobal.net',
              'yahoo.com',
              'icloud.com',
              'me.com',
              'mac.com',
            ]}
            parse={(value) => {
              if (source === 'publisherPrePingCriteria') {
                return value;
              } else {
                return value.join(',');
              }
            }}
            format={(value) => {
              if (source === 'publisherPrePingCriteria') {
                return Array.isArray(value) ? value : [];
              } else {
                if (!value) {
                  return [];
                } else if (typeof value !== 'string') {
                  return [value];
                }
                return value.split(',');
              }
            }}
          />
        </ColCondition>
      </SelectRow>
    </TabPane>
  );
};

export default AttributePane;
